import type { RuntimeWallet } from "../types/payments-wallets.types";

const isZero = (amount: string) => /^0*(\.0*)?$/.test(amount.trim());

/**
 * Whether the wallet looks never used, which is when the card offers "Delete": active, the three balances at
 * zero and no movement ever. The backend has the last word — it also refuses while a collection or payout in
 * that currency is still open, and answers 409 `wallet_in_use` with the reason.
 */
export function isWalletDeletable(wallet: RuntimeWallet): boolean {
  return (
    wallet.status === "active" &&
    !wallet.last_movement_at &&
    isZero(wallet.balance_available) &&
    isZero(wallet.balance_pending) &&
    isZero(wallet.balance_blocked)
  );
}

/** A copy of `items` with the one at `from` moved to `to`; the same array when either index is out of range. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return [...items];
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * `wallets` in the order of `ids`, the ones not named kept after them in their current order. What the list
 * looks like right after saving an order, without reading it again.
 */
export function orderWallets(wallets: readonly RuntimeWallet[], ids: readonly string[]): RuntimeWallet[] {
  const position = new Map(ids.map((id, index) => [id, index]));
  const placed = wallets
    .filter((wallet) => position.has(wallet.id))
    .sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0));
  return [...placed, ...wallets.filter((wallet) => !position.has(wallet.id))];
}
